import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Carousel } from 'foundations-components/transitional/components/Carousel';
import { Clickable } from 'foundations-components/transitional/components/Clickable';
import { Image } from 'foundations-components/transitional/components/Image';
import { ScrollContainer } from 'foundations-components/transitional/components/ScrollContainer';
import { ScrollingColumn } from 'foundations-components/transitional/components/ScrollingColumn';
import { usePrevious } from 'marketplace-ui-common/hooks/usePrevious';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Fullscreen } from './Fullscreen';
import { VideoPlayer } from './VideoPlayer';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const NoopOverhang = () => null;
export function MediaCarousel({
  items,
  onItemClick,
  onTrackInteraction,
  onFullscreenChange
}) {
  const [fullscreen, setFullscreen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const prevStepIndex = usePrevious(stepIndex);
  const imageElementRef = useRef(null);
  const {
    maxHeight
  } = useResponsiveProps({
    maxHeight: {
      xs: 300,
      sm: 480
    }
  });
  useLayoutEffect(() => {
    if (stepIndex !== prevStepIndex) {
      if (imageElementRef.current && imageElementRef.current.parentElement) {
        imageElementRef.current.parentElement.scrollTop = 0;
      }
    }
  }, [fullscreen, stepIndex, prevStepIndex]);
  const renderImage = useCallback(item => {
    const image = /*#__PURE__*/_jsx(Image, {
      ref: imageElementRef,
      src: item.source,
      alt: item.alt,
      shape: "rounded"
    });
    return fullscreen ? /*#__PURE__*/_jsx(ScrollingColumn, {
      maxHeight: "calc(100vh - 126px)",
      Overhang: NoopOverhang,
      children: image
    }) : /*#__PURE__*/_jsx(Clickable, {
      onClick: () => {
        onItemClick === null || onItemClick === void 0 || onItemClick(item);
        onTrackInteraction === null || onTrackInteraction === void 0 || onTrackInteraction({
          action: 'click carousel screenshot'
        });
        onFullscreenChange === null || onFullscreenChange === void 0 || onFullscreenChange(!fullscreen);
        setFullscreen(!fullscreen);
      },
      children: /*#__PURE__*/_jsx(View, {
        boxShadow: fullscreen ? '400' : undefined,
        children: /*#__PURE__*/_jsx(ScrollContainer, {
          maxHeight: maxHeight,
          Overhang: NoopOverhang,
          children: image
        })
      })
    });
  }, [fullscreen, imageElementRef, maxHeight, onItemClick, onTrackInteraction, onFullscreenChange]);
  return /*#__PURE__*/_jsx(Fullscreen, {
    fullscreen: fullscreen,
    onClose: () => {
      // Wait a frame so that marketplace-embed knows we were previously
      // fullscreen. Otherwise, the entire modal/panel will close.
      requestAnimationFrame(() => {
        setFullscreen(false);
        onFullscreenChange === null || onFullscreenChange === void 0 || onFullscreenChange(false);
      });
    },
    children: /*#__PURE__*/_jsx(Carousel, {
      padding: "0",
      maxWidth: "100%",
      stepIndex: stepIndex,
      onStepIndexChange: e => setStepIndex(e.target.value),
      allowStepLoop: true
      // content
      ,
      steps: items.map((item, index) => ({
        alt: item.alt || `Item ${index + 1}`,
        content: /*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          alignItems: "center",
          gap: "100",
          children: [item.type === 'image' ? renderImage(item) : /*#__PURE__*/_jsx(VideoPlayer, {
            videoUrl: item.source,
            onTrackInteraction: onTrackInteraction,
            thumbnailUrl: item.thumbnailUrl
          }), /*#__PURE__*/_jsxs(Text, {
            color: fullscreen ? 'fill-primary-disabled' : 'text-core-subtle',
            marginBlockStart: "100",
            typography: fullscreen ? undefined : 'detail-100',
            children: [`${index + 1}/${items.length}`, item.alt ? ` - ${item.alt}` : '']
          })]
        })
      }))
    })
  });
}